import { useState, useEffect, useRef } from 'react';
import { useAppContext } from '../../context/AppContext';
import { usePhotos } from '../../hooks/usePhotos';
import { useHunts } from '../../hooks/useHunts';
import { useAutoSelectPrimary } from '../../hooks/useAutoSelectPrimary';
import { formatLabel } from '../../lib/huntDomain';
import styles from './NumberDetailModal.module.css';

function PhotoMenu({ photo, isPrimary, onSetPrimary, onEditPlace, onDelete, deleting }) {
  const [open, setOpen] = useState(false);
  const [openLeft, setOpenLeft] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    function handleKeyDown(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  // The menu defaults to right-anchored (flush with the button's right edge).
  // For thumbnails in the OTHER SHOTS grid's first column, that pushes a
  // 160px-wide menu off the left edge of the screen — flip to left-anchored
  // (grows rightward instead) whenever right-anchoring would do that.
  useEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setOpenLeft(rect.right - 160 < 8);
  }, [open]);

  return (
    <div className={styles.overflowWrap} ref={ref}>
      <button
        className={styles.overflowBtn}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="true"
        aria-expanded={open}
      >
        ···
      </button>
      {open && (
        <div className={`${styles.overflowMenu} ${openLeft ? styles.overflowMenuLeft : ''}`} role="menu">
          {!isPrimary && (
            <button
              className={styles.overflowItem}
              role="menuitem"
              onClick={() => { setOpen(false); onSetPrimary(); }}
            >
              Set Primary
            </button>
          )}
          <button
            className={styles.overflowItem}
            role="menuitem"
            onClick={() => { setOpen(false); onEditPlace(); }}
          >
            Edit Place
          </button>
          <button
            className={`${styles.overflowItem} ${styles.overflowItemDanger}`}
            role="menuitem"
            onClick={() => onDelete()}
            disabled={deleting}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      )}
    </div>
  );
}

export default function NumberDetailModal({ layers }) {
  const { state, dispatch, derived, activeHunt } = useAppContext();
  const { setPrimary, updatePhoto, deletePhoto } = usePhotos();
  const { updateHuntList } = useHunts();
  const { runAutoSelect } = useAutoSelectPrimary();
  const { photosByItem, primaryByItem, sharedLocations } = derived;

  const itemKey = state.modalData?.itemKey;
  const photos  = photosByItem.get(itemKey) ?? [];
  const primary = primaryByItem.get(itemKey) ?? null;
  const otherShots = photos.filter(p => p.id !== primary?.id);
  const huntList = activeHunt?.huntList ?? [];
  const inHunt = huntList.includes(itemKey);
  const label = activeHunt ? formatLabel(activeHunt.type, itemKey) : itemKey;

  const [deleting, setDeleting] = useState(null);
  const [brokenIds, setBrokenIds] = useState(() => new Set());
  const [otherShotsOpen, setOtherShotsOpen] = useState(!!layers?.duplicates);
  const [editingPlace, setEditingPlace] = useState(false);
  const [placeValue, setPlaceValue] = useState(primary?.location ?? '');
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const [fixingShared, setFixingShared] = useState(false);

  const isShared = primary ? sharedLocations.has(primary.location) : false;

  if (!activeHunt) return null;

  function close() {
    dispatch({ type: 'CLOSE_MODAL' });
  }

  function handleLocationClick() {
    if (!primary) return;
    close();
    dispatch({ type: 'SET_VIEW', view: 'location', location: primary.location });
  }

  async function handleSetPrimary(photoId) {
    await setPrimary(itemKey, photoId);
  }

  async function handleToggleHunt() {
    await updateHuntList(activeHunt, inHunt ? huntList.filter(k => k !== itemKey) : [...huntList, itemKey]);
  }

  async function handleFixShared() {
    setFixingShared(true);
    try {
      await runAutoSelect();
    } finally {
      setFixingShared(false);
    }
  }

  async function handleDelete(photo) {
    if (!window.confirm(`Delete this photo from ${photo.location}?`)) return;
    setDeleting(photo.id);
    await deletePhoto(photo.id, itemKey);
    setDeleting(null);
    if (photos.length <= 1) close();
  }

  function startEditPlace() {
    setPlaceValue(primary?.location ?? '');
    setEditingPlace(true);
  }

  async function commitEditPlace() {
    const value = placeValue.trim();
    setEditingPlace(false);
    if (!primary || !value || value === primary.location) return;
    await updatePhoto(primary.id, { location: value });
  }

  async function editShotPlace(photo) {
    const value = window.prompt('Location', photo.location);
    if (!value || !value.trim() || value.trim() === photo.location) return;
    await updatePhoto(photo.id, { location: value.trim() });
  }

  if (!photos.length || !primary) {
    return (
      <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && close()}>
        <div className={styles.panel}>
          <div className={styles.headerRow}>
            <h2 className={styles.numberDisplay}>{label}</h2>
            <button className={styles.closeBtn} onClick={close} aria-label="Close">✕</button>
          </div>
          <p className={styles.empty}>No photos yet for {label}.</p>
          <button
            className={styles.addShotBtn}
            onClick={() => dispatch({ type: 'OPEN_MODAL', modal: 'addPhoto', data: { itemKey } })}
          >
            ADD SHOT
          </button>
        </div>
      </div>
    );
  }

  const thumbSrc = primary.thumbnailDataUrl || primary.thumbnailUrl || null;
  const heroBroken = brokenIds.has(primary.id);

  return (
    <>
    <div className={styles.backdrop} onClick={(e) => e.target === e.currentTarget && close()}>
      <div className={styles.panel}>
        <div className={styles.headerRow}>
          <button className={styles.closeBtn} onClick={close} aria-label="Close">✕</button>
        </div>

        <div className={styles.numberBlock}>
          <h2 className={styles.numberDisplay}>{label}</h2>
          {editingPlace ? (
            <input
              className={styles.placeInput}
              value={placeValue}
              onChange={e => setPlaceValue(e.target.value)}
              onBlur={commitEditPlace}
              onKeyDown={e => e.key === 'Enter' && e.currentTarget.blur()}
              autoFocus
            />
          ) : (
            <button className={styles.placeLine} onClick={handleLocationClick}>
              {primary.location.toUpperCase()} · {new Date(primary.addedAt).toLocaleDateString('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' })}
            </button>
          )}
        </div>

        <div className={styles.hero}>
          {thumbSrc && !heroBroken ? (
            <img
              src={thumbSrc}
              alt={`${label} at ${primary.location}`}
              className={styles.heroImg}
              onError={() => setBrokenIds(prev => new Set(prev).add(primary.id))}
            />
          ) : (
            <div className={styles.heroPlaceholder}>{thumbSrc ? 'Image unavailable' : 'No image'}</div>
          )}
          <span className={styles.markPrimary}>PRIMARY</span>
          {isShared && <span className={styles.markSharedPlace}>×2 SHARED PLACE</span>}
        </div>

        {isShared && (
          <div className={styles.sharedBanner}>
            <span>{primary.location} is primary for another item too.</span>
            <button
              className={styles.sharedFixBtn}
              onClick={handleFixShared}
              disabled={fixingShared}
              title="Runs auto-select primary photos across all items in this hunt"
            >
              {fixingShared ? 'Fixing…' : 'Auto-fix'}
            </button>
          </div>
        )}

        <div className={styles.actionsRow}>
          <button
            className={styles.addShotBtn}
            onClick={() => dispatch({ type: 'OPEN_MODAL', modal: 'addPhoto', data: { itemKey } })}
          >
            ADD SHOT
          </button>
          {activeHunt.type !== 'custom' && (
            <button
              className={`${styles.huntBtn} ${inHunt ? styles.huntActive : ''}`}
              onClick={handleToggleHunt}
            >
              HUNT
            </button>
          )}
          <PhotoMenu
            photo={primary}
            isPrimary
            onSetPrimary={() => {}}
            onEditPlace={startEditPlace}
            onDelete={() => handleDelete(primary)}
            deleting={deleting === primary.id}
          />
        </div>

        {otherShots.length > 0 && (
          <div className={styles.otherShots}>
            <button
              className={styles.otherShotsToggle}
              onClick={() => setOtherShotsOpen(o => !o)}
              aria-expanded={otherShotsOpen}
            >
              OTHER SHOTS · {otherShots.length} <span className={styles.caret}>{otherShotsOpen ? '▴' : '▾'}</span>
            </button>

            {otherShotsOpen && (
              <div className={styles.otherShotsGrid}>
                {otherShots.map(photo => {
                  const shotSrc = photo.thumbnailDataUrl || photo.thumbnailUrl || null;
                  const shotBroken = brokenIds.has(photo.id);
                  return (
                    <div key={photo.id} className={styles.otherShotCard}>
                      <button
                        type="button"
                        className={styles.otherShotImgWrap}
                        onClick={() => shotSrc && !shotBroken && setPreviewPhoto(photo)}
                        aria-label={`View photo at ${photo.location} larger`}
                      >
                        {shotSrc && !shotBroken ? (
                          <img
                            src={shotSrc}
                            alt={`${label} at ${photo.location}`}
                            className={styles.otherShotImg}
                            onError={() => setBrokenIds(prev => new Set(prev).add(photo.id))}
                          />
                        ) : (
                          <div className={styles.otherShotPlaceholder}>—</div>
                        )}
                      </button>
                      <span className={styles.otherShotCaption}>{photo.location}</span>
                      <PhotoMenu
                        photo={photo}
                        isPrimary={false}
                        onSetPrimary={() => handleSetPrimary(photo.id)}
                        onEditPlace={() => editShotPlace(photo)}
                        onDelete={() => handleDelete(photo)}
                        deleting={deleting === photo.id}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    {previewPhoto && (
      <div className={styles.lightboxBackdrop} onClick={() => setPreviewPhoto(null)}>
        <img
          src={previewPhoto.thumbnailDataUrl || previewPhoto.thumbnailUrl}
          alt={`${label} at ${previewPhoto.location}`}
          className={styles.lightboxImg}
        />
        <span className={styles.lightboxCaption}>{previewPhoto.location}</span>
        <button
          type="button"
          className={styles.lightboxClose}
          onClick={() => setPreviewPhoto(null)}
          aria-label="Close"
        >
          ✕
        </button>
      </div>
    )}
    </>
  );
}
